import React, { useState, useEffect, useContext } from 'react';
import './MegaMillionsOverride.css';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  useToast,
  useDisclosure,
  Badge,
  Flex,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  Select,
  Checkbox,
  Image,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaCoins, FaTrophy, FaChartLine, FaUserAlt, FaArrowLeft, FaInfoCircle, FaGift, FaWallet, FaPlus, FaMinus, FaTrash, FaCheck, FaSave, FaGamepad, FaCalendarAlt } from 'react-icons/fa';
import { GiTreasureMap, GiCardRandom, GiCardPlay, GiChest } from 'react-icons/gi';
import { useBalance } from '../../../context/BalanceContext';
import ScratchCardDisplay from './ScratchCardDisplay';
import { getCardConfigById, CASH_KING_CONFIG } from './Games/ScratchCardConfigs';
import BaseBountyPool from './BountyPool/BaseBountyPool';
import { QuickCashBountyPool, CashKingBountyPool, JackpotMillionsBountyPool } from './BountyPool';
import CashKingGame from './Games/CashKing/CashKingGame';
import JackpotMillionsGame from './Games/JackpotMillions/JackpotMillionsGame';
import { getOrCreateBatch, mintCardFromBatch } from './Randomness/SegmentManager';
import { NFTManager } from '../../../engine/modules/NFTManager';
import { useWallet } from '../../../contexts/WalletContext';
import SegmentVerifier from './Randomness/SegmentVerifier';
import scratchCardService from '../../../services/scratchCardService';

/**
 * ScratchCardGame Component
 * 
 * Main game interface for playing scratch card games.
 * Handles game state, user interactions, and displays the scratch card.
 */
const ScratchCardGame = ({ inlineMode, onBack, cardId: propCardId, gameConfig, onWin, onLose }) => {
  // Get wallet from context
  const wallet = useWallet();
  // Use cardId from props if provided, otherwise from URL params
  const { cardId: urlCardId } = useParams();
  const cardId = propCardId || urlCardId;
  
  // Debug the cardId
  console.log('ScratchCardGame - cardId:', cardId, 'urlCardId:', urlCardId, 'propCardId:', propCardId);
  
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Get card type from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const urlCardType = urlParams.get('type');
  
  // Force dark red theme for Mega Millions
  useEffect(() => {
    if (urlCardType === 'mega_millions' || cardId === 'mega_millions') {
      console.log('Forcing dark red theme for Mega Millions');
      
      // Use setTimeout to ensure the DOM has been rendered
      setTimeout(() => {
        // Find all panels by their heading text
        document.querySelectorAll('.chakra-heading').forEach(heading => {
          if (heading.textContent.includes('Game Controls') || heading.textContent.includes('Create Your Game')) {
            const panel = heading.closest('.chakra-box');
            if (panel) {
              console.log('Found panel, applying dark red style:', heading.textContent);
              panel.style.background = 'linear-gradient(145deg, #660000, #8B0000)';
              panel.style.border = '1px solid rgba(220, 20, 60, 0.3)';
            }
          }
        });
      }, 100);
    }
  }, [urlCardType, cardId]);
  
  // Function to handle navigation back to all cards
  const handleBackToAllCards = () => {
    if (inlineMode && onBack) {
      // If in inline mode, use the provided onBack function
      onBack();
    } else {
      // Otherwise, navigate to the scratch cards marketplace
      navigate('/scratch-cards');
    }
  };
  
  // Game state
  const [cardConfig, setCardConfig] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [gameResult, setGameResult] = useState(null);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [nftManager, setNftManager] = useState(null);
  
  // Use both local state and global context for balance
  const { balance, tokens, setTokens } = useBalance();
  const [localBalance, setLocalBalance] = useState(balance.jinu || 1500);
  const userBalance = localBalance;
  
  // Function to update both local and global balances
  const updateJinuBalance = (amount) => {
    // Update local balance
    setLocalBalance(prev => {
      const newBalance = prev + amount;
      
      // Update global balance in BalanceContext
      // Update the balance object
      balance.jinu = newBalance;
      
      // Update the tokens array
      const updatedTokens = tokens.map(token => 
        token.symbol === 'JINU' ? {...token, balance: newBalance} : token
      );
      setTokens(updatedTokens);
      
      return newBalance;
    });
  };
  
  // Get card-specific colors based on cardId
  const getCardColors = (id) => {
    // Normalize the ID to ensure consistent comparison
    const normalizedId = String(id).toLowerCase().trim();
    
    // Debug the card ID being processed
    console.log('getCardColors - processing card ID:', normalizedId);
    
    // Special case for quick_cash_premium - always map to mega_millions theme
    if (normalizedId === 'quick_cash_premium') {
      console.log('Quick Cash Premium detected - using Mega Millions dark red theme');
      return {
        primary: '#8B0000', // Dark red background
        secondary: '#660000', // Darker red for containers
        accent: '#DC143C', // Crimson accent
        text: 'white',
        button: 'red',
        buttonVariant: 'solid',
        progressColor: 'red.400'
      };
    }
    
    switch (normalizedId) {
      case 'cash_king':
        return {
          primary: '#1b4332', // Main dark green background
          secondary: '#0d3b1f', // Darker green for containers
          accent: 'yellow.400',
          text: '#4fd1c5',
          button: 'green',
          buttonVariant: 'solid',
          progressColor: 'green.400'
        };
      case 'quick_cash':
        return {
          primary: 'blue.900',
          secondary: 'blue.800',
          accent: 'blue.400',
          text: 'white',
          button: 'blue',
          buttonVariant: 'solid',
          progressColor: 'blue.400'
        };
      case 'jackpot_millions':
        return {
          primary: 'purple.900',
          secondary: 'purple.800',
          accent: 'purple.400',
          text: 'white',
          button: 'purple',
          buttonVariant: 'solid',
          progressColor: 'purple.400'
        };
      case 'mega_millions':
      case 'quick_cash_premium': // Add case for quick_cash_premium to use the same dark red theme
        return {
          primary: '#8B0000', // Dark red background
          secondary: '#660000', // Darker red for containers
          accent: '#DC143C', // Crimson accent
          text: 'white',
          button: 'red',
          buttonVariant: 'solid',
          progressColor: 'red.400'
        };
      default:
        console.log('No specific color theme found for card ID:', id, 'using default blue theme');
        return {
          primary: 'blue.900',
          secondary: 'blue.800',
          accent: 'blue.400',
          text: 'white',
          button: 'blue',
          buttonVariant: 'solid',
          progressColor: 'blue.400'
        };
    }
  };

  // Initialize NFT Manager
  useEffect(() => {
    const initNftManager = async () => {
      try {
        // Create a new NFT Manager instance
        const manager = new NFTManager();
        
        // Initialize without Solana connection for now
        // In production, you would pass a Solana connection here
        await manager.initialize();
        
        setNftManager(manager);
        console.log('NFT Manager initialized');
      } catch (error) {
        console.error('Error initializing NFT Manager:', error);
      }
    };
    
    initNftManager();
  }, []);
  
  // Initialize or get batch for this game
  useEffect(() => {
    if (cardConfig && !currentBatch) {
      try {
        // Get or create a batch for this game
        const batch = getOrCreateBatch(cardConfig);
        setCurrentBatch(batch);
        console.log('Using batch:', batch.id);
      } catch (error) {
        console.error('Error initializing batch:', error);
        toast({
          title: "Error",
          description: "Failed to initialize game. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    }
  }, [cardConfig, currentBatch]);

  // Load card configuration
  useEffect(() => {
    console.log('ScratchCardGame - Loading card config for ID:', cardId);
    
    // Special case for Cash King
    if (cardId === 'cash_king') {
      console.log('ScratchCardGame - Special case for Cash King');
      setCardConfig(CASH_KING_CONFIG);
      return;
    }
    
    const config = getCardConfigById(cardId);
    if (!config) {
      toast({
        title: "Card not found",
        description: "This scratch card game doesn't exist.",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      navigate('/scratch-cards');
      return;
    }
    
    console.log('ScratchCardGame - Loaded card config:', config.id);
    setCardConfig(config);
  }, [cardId, navigate, toast]);
  
  // Convert prize value string to number (e.g., '5K' to 5000)
  const convertPrizeToNumber = (prizeString) => {
    if (!prizeString) return 0;
    
    console.log('DEBUG: Converting prize string to number:', prizeString);
    
    let result = 0;
    if (prizeString.includes('K')) {
      result = parseInt(prizeString.replace('K', '')) * 1000;
    } else {
      result = parseInt(prizeString);
    }
    
    console.log('DEBUG: Converted prize result:', result);
    return result;
  };
  
  // Get a random prize based on the prize distribution in the config
  const getRandomPrize = (config) => {
    if (!config || !config.bountyPool || !config.bountyPool.tiers) {
      // Fallback if config is not properly structured
      return '200';
    }
    
    // Create a weighted array of all prizes based on their count
    const allPrizes = [];
    config.bountyPool.tiers.forEach(tier => {
      tier.prizes.forEach(prize => {
        // Convert prize amount to display format (e.g., 5000 to '5K')
        const prizeDisplay = prize.amount >= 1000 
          ? `${prize.amount / 1000}K` 
          : prize.amount.toString();
          
        // Add this prize to the array based on its count (weighted random selection)
        for (let i = 0; i < prize.count; i++) {
          allPrizes.push(prizeDisplay);
        }
      });
    });
    
    // Select a random prize from the weighted array
    if (allPrizes.length > 0) {
      const randomIndex = Math.floor(Math.random() * allPrizes.length);
      return allPrizes[randomIndex];
    }
    
    return '200'; // Fallback
  };
  
  // Initialize game from a minted card
  const initializeGameFromCard = (card) => {
    const totalCells = cardConfig.gridSize.rows * cardConfig.gridSize.columns;
    const cellValues = [];
    const winningCells = [];
    
    // Handle cards from the on-the-fly generation system
    if (card.isWinner !== undefined) {
      // This is a card from the on-the-fly system
      const isWinningCard = card.isWinner;
      const winningPrizeValue = isWinningCard ? card.prizeAmount.toString() : null;
      
      // If the card already has grid details from the server, use them
      if (card.gridDetails && card.gridDetails.cellValues) {
        return {
          status: 'active',
          cellValues: card.gridDetails.cellValues,
          winningCells: card.gridDetails.winningCells || [],
          revealedCells: [],
          isWinningCard,
          winAmount: card.prizeAmount || 0,
          winningSymbolsRevealed: false,
          prizeValues: [winningPrizeValue],
          winningPrizeValue,
          cardId: card.id || card.cardId, // Store the card ID for API operations
          batchId: card.batchId,
          segmentId: card.segmentId,
          cardIndex: card.cardIndex,
          verification: card.verification
        };
      }
    }
    
    // For legacy cards or if grid details aren't provided
    // Determine if this card is a winner based on the prize
    const isWinningCard = card.prize?.isWinning || card.isWinner || false;
    const winningPrizeValue = isWinningCard ? (card.prize?.value || card.prizeAmount?.toString()) : null;
    
    // For winning cards, create a grid with matching symbols
    if (isWinningCard) {
      // Select 3 random positions for the winning value
      const winPositions = [];
      while (winPositions.length < 3) {
        const randomPos = Math.floor(Math.random() * totalCells);
        if (!winPositions.includes(randomPos)) {
          winPositions.push(randomPos);
        }
      }
      
      // Fill the grid with values
      for (let i = 0; i < totalCells; i++) {
        if (winPositions.includes(i)) {
          // Put the winning value in the selected positions
          cellValues.push(winningPrizeValue);
        } else {
          // For non-winning positions, use values that won't create additional matches
          const availablePrizes = cardConfig.prizes.filter(prize => prize !== winningPrizeValue);
          const randomIndex = Math.floor(Math.random() * availablePrizes.length);
          const randomPrize = availablePrizes[randomIndex];
          
          // Check if adding this prize would create an unintended match
          const existingCount = cellValues.filter(value => value === randomPrize).length;
          if (existingCount >= 2) {
            // If this would create an unintended match, use a different prize
            const alternativePrize = availablePrizes[(randomIndex + 1) % availablePrizes.length];
            cellValues.push(alternativePrize);
          } else {
            cellValues.push(randomPrize);
          }
        }
      }
    } else {
      // For non-winning cards, ensure no 3 matching values
      // First fill the grid with random values
      for (let i = 0; i < totalCells; i++) {
        const randomPrizeIndex = Math.floor(Math.random() * cardConfig.prizes.length);
        const randomPrize = cardConfig.prizes[randomPrizeIndex];
        
        // Check if adding this prize would create a match
        const existingCount = cellValues.filter(value => value === randomPrize).length;
        if (existingCount >= 2) {
          // If this would create a match, use a different prize
          const alternativePrize = cardConfig.prizes[(randomPrizeIndex + 1) % cardConfig.prizes.length];
          cellValues.push(alternativePrize);
        } else {
          cellValues.push(randomPrize);
        }
      }
    }
    
    // Calculate win amount based on the prize
    const winAmount = isWinningCard ? card.prize.amount : 0;
    
    // Create the game state
    return {
      status: 'active',
      cellValues,
      winningCells,
      revealedCells: [],
      isWinningCard,
      winAmount,
      winningSymbolsRevealed: false,
      prizeValues: [card.prize.value], // Store the prize value for this card
      winningPrizeValue, // Store the winning prize value
      batchId: card.batchId,
      segmentId: card.segmentId,
      cardIndex: card.cardIndex,
      verification: card.verification
    };
  };
  
  // Original initialize game state (kept for backward compatibility)
  const initializeGame = (isWinning = false) => {
    const totalCells = cardConfig.gridSize.rows * cardConfig.gridSize.columns;
    const cellValues = [];
    const winningCells = [];
    
    // Determine if this card is a winner based on the configured probability
    const isWinningCard = isWinning || (Math.random() < cardConfig.winProbability);
    
    // Select prize values based on the configured prize distribution
    const selectedPrizes = [];
    const winCount = cardConfig.winningSymbols || 3;
    
    // This will track which prize value is used for the winning match
    let winningPrizeValue = null;
    
    if (isWinningCard) {
      // For winning cards, select a prize based on the actual prize distribution
      // This ensures the proper odds for each prize tier
      const mainPrize = getRandomPrize(cardConfig);
      selectedPrizes.push(mainPrize);
      
      // Add additional winning symbols with lower values
      for (let i = 1; i < winCount; i++) {
        // For additional winning symbols, use lower prize values
        const lowerPrizes = cardConfig.prizes.filter(prize => {
          const prizeValue = convertPrizeToNumber(prize);
          const mainPrizeValue = convertPrizeToNumber(mainPrize);
          return prizeValue < mainPrizeValue;
        });
        
        if (lowerPrizes.length > 0) {
          const randomIndex = Math.floor(Math.random() * lowerPrizes.length);
          selectedPrizes.push(lowerPrizes[randomIndex]);
        } else {
          // Fallback if no lower prizes available
          selectedPrizes.push(mainPrize);
        }
      }
      
      // Shuffle the prizes to randomize their positions
      selectedPrizes.sort(() => Math.random() - 0.5);
      
      // Randomly select winning cell indices
      while (winningCells.length < winCount) {
        const randomIndex = Math.floor(Math.random() * totalCells);
        if (!winningCells.includes(randomIndex)) {
          winningCells.push(randomIndex);
        }
      }
    } else {
      // For non-winning cards, just use random low-value prizes that don't match
      for (let i = 0; i < winCount; i++) {
        const lowerPrizes = cardConfig.prizes.slice(0, 4); // Use the lowest prize values
        const randomIndex = Math.floor(Math.random() * lowerPrizes.length);
        selectedPrizes.push(lowerPrizes[randomIndex]);
      }
      
      // Make sure prizes don't match by adjusting if needed
      if (selectedPrizes.every(prize => prize === selectedPrizes[0])) {
        if (selectedPrizes.length > 1) {
          selectedPrizes[1] = cardConfig.prizes[0];
        }
        if (selectedPrizes.length > 2) {
          selectedPrizes[2] = cardConfig.prizes[1];
        }
      }
    }
    
    // For winning cards, we need to ensure there are 3 matching values that match one of the winning symbols
    if (isWinningCard) {
      // Choose one of the selected prizes to be the winning match
      winningPrizeValue = selectedPrizes[Math.floor(Math.random() * selectedPrizes.length)];
      
      // Select 3 random positions for the winning value
      const winPositions = [];
      while (winPositions.length < 3) {
        const randomPos = Math.floor(Math.random() * totalCells);
        if (!winPositions.includes(randomPos)) {
          winPositions.push(randomPos);
          // We no longer need to track these as winning cells since we'll use isMatch instead
        }
      }
      
      // Fill the grid with values
      for (let i = 0; i < totalCells; i++) {
        if (winPositions.includes(i)) {
          // Put the winning value in the selected positions
          cellValues.push(winningPrizeValue);
        } else {
          // For non-winning positions, use values that won't create additional matches
          // Avoid using the winning value or creating other 3-of-a-kind matches
          const availablePrizes = cardConfig.prizes.filter(prize => prize !== winningPrizeValue);
          const randomIndex = Math.floor(Math.random() * availablePrizes.length);
          const randomPrize = availablePrizes[randomIndex];
          
          // Check if adding this prize would create an unintended match
          const existingCount = cellValues.filter(value => value === randomPrize).length;
          if (existingCount >= 2) {
            // If this would create an unintended match, use a different prize
            const alternativePrize = availablePrizes[(randomIndex + 1) % availablePrizes.length];
            cellValues.push(alternativePrize);
          } else {
            cellValues.push(randomPrize);
          }
        }
      }
    } else {
      // For non-winning cards, ensure no 3 matching values that match the winning symbols
      // First fill the grid with random values
      for (let i = 0; i < totalCells; i++) {
        const randomPrizeIndex = Math.floor(Math.random() * cardConfig.prizes.length);
        const randomPrize = cardConfig.prizes[randomPrizeIndex];
        
        // Check if adding this prize would create a match
        const existingCount = cellValues.filter(value => value === randomPrize).length;
        if (existingCount >= 2) {
          // If this would create a match, use a different prize
          const alternativePrize = cardConfig.prizes[(randomPrizeIndex + 1) % cardConfig.prizes.length];
          cellValues.push(alternativePrize);
        } else {
          cellValues.push(randomPrize);
        }
      }
      
      // Verify no unintended matches
      for (const prize of selectedPrizes) {
        const count = cellValues.filter(value => value === prize).length;
        if (count >= 3) {
          // If we accidentally created a match, replace one occurrence
          const indexToReplace = cellValues.findIndex(value => value === prize);
          if (indexToReplace !== -1) {
            // Replace with a different prize
            const alternativePrizes = cardConfig.prizes.filter(p => p !== prize);
            cellValues[indexToReplace] = alternativePrizes[Math.floor(Math.random() * alternativePrizes.length)];
          }
        }
      }
    }
    
    // Calculate win amount based on the matching prize value
    let winAmount = 0;
    if (isWinningCard && winningPrizeValue) {
      // The win amount is the value of the matching prize
      winAmount = convertPrizeToNumber(winningPrizeValue);
    }
    
    // Create the game state
    return {
      status: 'active',
      cellValues,
      winningCells,
      revealedCells: [],
      isWinningCard,
      winAmount,
      winningSymbolsRevealed: false, // Initially, winning symbols are not revealed
      prizeValues: selectedPrizes, // Store the selected prize values for this card
      winningPrizeValue // Store the prize value that creates the winning match
    };
  };

  // Handle buying a card
  const handleBuy = async () => {
    if (!cardConfig || !cardConfig.cost) {
      console.error('Card configuration is missing or invalid');
      return;
    }

    // Check if the user has enough balance
    if (userBalance < cardConfig.cost) {
      toast({
        title: 'Insufficient Balance',
        description: `You need ${cardConfig.cost} JINU to play this game.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Temporarily deduct the cost from the user's balance
      // (Will be properly deducted by the API)
      updateJinuBalance(-cardConfig.cost);
      
      // Draw a card from the on-the-fly system
      const drawnCard = await scratchCardService.drawCard(gameConfig.id);
      
      // Initialize game state based on the drawn card
      const gameState = initializeGameFromCard(drawnCard);
      
      // Reset any existing state in the BaseCardComponent
      setGameState(null);
      
      // Use setTimeout to ensure the component has time to reset before setting the new state
      setTimeout(() => {
        setGameState(gameState);
      }, 50);
      
      // Close any open modals
      if (isOpen) {
        onClose();
      }
      
      // Reset game result
      setGameResult(null);
      
      toast({
        title: "Card purchased",
        description: `You've purchased a ${cardConfig.name} card.`,
        status: "success",
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error buying card:', error);
      toast({
        title: "Error",
        description: "Failed to purchase card. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    }
  };
  
  // Check for winning combinations in the revealed cells
  const checkForWinningCombination = () => {
    if (!gameState || !gameState.cellValues) return null;
    
    console.log('DEBUG: Checking for winning combination');
    console.log('DEBUG: Revealed cells:', gameState.revealedCells);
    console.log('DEBUG: Cell values:', gameState.cellValues);
    console.log('DEBUG: Prize values:', gameState.prizeValues);
    
    // Count occurrences of each value in the revealed cells
    const valueCounts = {};
    gameState.revealedCells.forEach(index => {
      const value = gameState.cellValues[index];
      if (value) { // Only count non-empty values
        valueCounts[value] = (valueCounts[value] || 0) + 1;
      }
    });
    
    console.log('DEBUG: Value counts:', valueCounts);
    
    // Check if any value appears 3 or more times
    for (const [value, count] of Object.entries(valueCounts)) {
      if (count >= 3 && gameState.prizeValues && gameState.prizeValues.includes(value)) {
        // Found a winning combination
        console.log('DEBUG: Found winning combination:', value, 'with count:', count);
        return value;
      }
    }
    
    console.log('DEBUG: No winning combination found');
    return null;
  };
  
  // Get the prize amount based on the matched value
  const getPrizeAmountFromMatchedValue = (matchedValue) => {
    if (!matchedValue) return 0;
    
    console.log('DEBUG: Getting prize amount from matched value:', matchedValue);
    const prizeAmount = convertPrizeToNumber(matchedValue);
    console.log('DEBUG: Prize amount from matched value:', prizeAmount);
    return prizeAmount;
  };
  
  // Handle revealing winning symbols first, then individual cells
  const handleReveal = async (index, value, isWinning, isRevealAll = false) => {
    if (!gameState || gameState.status !== 'active') return;
    
    // If this is a reveal all action and we're using the on-the-fly system (has cardId)
    if (isRevealAll && gameState.cardId) {
      try {
        // Reveal the card in the on-the-fly system
        await scratchCardService.revealCard(gameState.cardId);
      } catch (error) {
        console.error('Error revealing card in on-the-fly system:', error);
        // Continue with local reveal even if the API call fails
      }
    }
    
    // Create a copy of the current game state
    const newState = { ...gameState };
    
    // If winning symbols haven't been revealed yet, reveal them first
    if (!newState.winningSymbolsRevealed) {
      newState.winningSymbolsRevealed = true;
      
      // Determine the potential win amount based on the prize values
      if (newState.isWinningCard && newState.prizeValues && newState.prizeValues.length > 0) {
        // Select the highest prize value for this winning card
        const highestPrize = newState.prizeValues.reduce((max, prize) => {
          const prizeValue = convertPrizeToNumber(prize);
          return prizeValue > max ? prizeValue : max;
        }, 0);
        
        // Set the potential win amount
        newState.potentialWinAmount = highestPrize;
      }
      
      setGameState(newState);
      
      // Show toast for winning symbols revealed
      toast({
        title: "Winning Symbols Revealed",
        description: "Now scratch the card to reveal your symbols!",
        status: "info",
        duration: 2000,
        isClosable: true
      });
      
      return; // Exit early, wait for next click to reveal cells
    }
    
    // Add this cell to the revealed cells if it's not already there
    if (!newState.revealedCells.includes(index)) {
      newState.revealedCells.push(index);
    }
    
    // Check if this is a winning cell
    if (isWinning) {
      // Count how many winning cells have been revealed
      const revealedWinningCells = newState.revealedCells.filter(cellIndex => 
        newState.winningCells.includes(cellIndex)
      );
      
      // If we've revealed enough winning cells, the player wins
      if (revealedWinningCells.length >= cardConfig.winningSymbols) {
        newState.status = 'won';
        
        // Use the potential win amount that was determined when revealing the winning symbols
        let winAmount = newState.potentialWinAmount || 0;
        
        // If no potential win amount was set, determine it based on the card type
        if (!winAmount && cardConfig.id === 'quick_cash') {
          // If we have prize values in the game state, use the highest one
          if (newState.prizeValues && newState.prizeValues.length > 0) {
            winAmount = convertPrizeToNumber(newState.prizeValues[0]);
          } else {
            // Fallback to the old probability-based prize determination
            const prizeTiers = [
              { match: 3, value: 100000, probability: 0.0001 },
              { match: 3, value: 50000, probability: 0.0002 },
              { match: 3, value: 20000, probability: 0.0005 },
              { match: 3, value: 10000, probability: 0.001 },
              { match: 3, value: 5000, probability: 0.001 },
              { match: 3, value: 2500, probability: 0.002 },
              { match: 3, value: 1000, probability: 0.005 },
              { match: 3, value: 500, probability: 0.01 },
              { match: 3, value: 300, probability: 0.02 },
              { match: 3, value: 200, probability: 0.42 }
            ];
            
            // Determine prize based on probability
            const random = Math.random();
            let cumulativeProbability = 0;
            
            for (const tier of prizeTiers) {
              cumulativeProbability += tier.probability;
              if (random <= cumulativeProbability) {
                winAmount = tier.value;
                break;
              }
            }
          }
        }
        
        // Update the win amount in the game state
        newState.winAmount = winAmount;
        
        // If we're using the on-the-fly system (has cardId), claim the prize through the API
        if (gameState.cardId) {
          // We'll claim the prize asynchronously
          scratchCardService.claimPrize(gameState.cardId)
            .then(claimedCard => {
              if (claimedCard && claimedCard.prizeAmount) {
                // Update balance with the prize amount from the server
                updateJinuBalance(claimedCard.prizeAmount);
                
                // Update game result if needed
                if (claimedCard.prizeAmount !== winAmount) {
                  setGameResult({
                    symbol: value,
                    amount: claimedCard.prizeAmount
                  });
                }
              }
            })
            .catch(error => {
              console.error('Error claiming prize in on-the-fly system:', error);
              // Fall back to local balance update if API fails
              updateJinuBalance(winAmount);
            });
        } else {
          // Fallback: Update user balance locally
          updateJinuBalance(winAmount);
        }
        
        // Set game result for modal
        setGameResult({
          symbol: value,
          amount: winAmount
        });
        
        // Show win modal
        onOpen();
      }
    }
    
    // Check if all cells have been revealed
    const totalCells = cardConfig.gridSize.rows * cardConfig.gridSize.columns;
    if (newState.revealedCells.length === totalCells || isRevealAll) {
      // If all cells are revealed and the game isn't won, it's lost
      if (newState.status !== 'won') {
        newState.status = 'lost';
      }
    }
    
    // Update game state
    setGameState(newState);
  };
  
  // Handle revealing all cells
  const handleRevealAll = () => {
    if (!gameState || gameState.status !== 'active') return;
    
    // If winning symbols haven't been revealed yet, reveal them first
    if (!gameState.winningSymbolsRevealed) {
      // Determine the potential win amount based on the prize values
      let potentialWinAmount = 0;
      if (gameState.isWinningCard && gameState.prizeValues && gameState.prizeValues.length > 0) {
        // Select the highest prize value for this winning card
        potentialWinAmount = gameState.prizeValues.reduce((max, prize) => {
          const prizeValue = convertPrizeToNumber(prize);
          return prizeValue > max ? prizeValue : max;
        }, 0);
      }
      
      const newState = { 
        ...gameState, 
        winningSymbolsRevealed: true,
        potentialWinAmount: potentialWinAmount
      };
      setGameState(newState);
      
      // Show toast for winning symbols revealed
      toast({
        title: "Winning Symbols Revealed",
        description: "Now revealing all cells...",
        status: "info",
        duration: 2000,
        isClosable: true
      });
      
      // Wait a moment for the winning symbols animation to complete
      setTimeout(() => {
        // Then trigger the reveal all
        setGameState(prevState => ({
          ...prevState,
          revealAll: true // This flag will trigger the revealAllCells function in BaseCardComponent
        }));
      }, 2500); // Increased delay to allow for the flip animation
      
      return;
    }
    
    // If winning symbols are already revealed, just reveal all cells
    const newState = { 
      ...gameState,
      revealAll: true // This flag will trigger the revealAllCells function in BaseCardComponent
    };
    
    // Update the game state to trigger the reveal all in BaseCardComponent
    setGameState(newState);
    
    // Process all cells for win detection
    const totalCells = cardConfig.gridSize.rows * cardConfig.gridSize.columns;
    const symbolCounts = {};
    let winningSymbol = null;
    let winAmount = 0;
    
    // Check for winning combinations
    if (gameState.isWinningCard) {
      // Count winning symbols
      const symbol = cardConfig.winningSymbol || '⭐';
      symbolCounts[symbol] = cardConfig.winningSymbols; // We know we have enough matches to win
      winningSymbol = symbol;
      
      // Use the potential win amount that was determined when revealing the winning symbols
      if (gameState.potentialWinAmount) {
        winAmount = gameState.potentialWinAmount;
      } 
      // If no potential win amount was set, determine it based on the prize values
      else if (gameState.prizeValues && gameState.prizeValues.length > 0) {
        // Use the highest prize value
        winAmount = gameState.prizeValues.reduce((max, prize) => {
          const prizeValue = convertPrizeToNumber(prize);
          return prizeValue > max ? prizeValue : max;
        }, 0);
      }
      // Fallback to the old probability-based prize determination
      else if (cardConfig.id === 'quick_cash') {
        const prizeTiers = [
          { match: 3, value: 100000, probability: 0.0001 },
          { match: 3, value: 50000, probability: 0.0002 },
          { match: 3, value: 20000, probability: 0.0005 },
          { match: 3, value: 10000, probability: 0.001 },
          { match: 3, value: 5000, probability: 0.001 },
          { match: 3, value: 2500, probability: 0.002 },
          { match: 3, value: 1000, probability: 0.005 },
          { match: 3, value: 500, probability: 0.01 },
          { match: 3, value: 300, probability: 0.02 },
          { match: 3, value: 200, probability: 0.42 }
        ];
        
        // Determine prize based on probability
        const random = Math.random();
        let cumulativeProbability = 0;
        
        for (const tier of prizeTiers) {
          cumulativeProbability += tier.probability;
          if (random <= cumulativeProbability) {
            winAmount = tier.value;
            break;
          }
        }
      }
      
      // Update game state for win
      if (winAmount > 0) {
        // Wait for the cell reveal animation to complete
        const allRevealed = newState.revealedCells.length === totalCells;
        
        // Check for winning combinations
        const winningValue = checkForWinningCombination();
        console.log('DEBUG: Winning value from checkForWinningCombination:', winningValue);
        
        // If we have a winning combination or all cells are revealed
        if (winningValue || allRevealed) {
          if (winningValue) {
            // Player won by matching 3 values
            newState.status = 'won';
            
            // IMPORTANT: Use the actual matched value for the prize amount
            // This ensures the prize amount always matches the symbols matched
            const winAmount = getPrizeAmountFromMatchedValue(winningValue);
            console.log('DEBUG: Prize amount from matched value:', winAmount);
            
            // Store the matched value and win amount in the game state
            newState.matchedValue = winningValue;
            newState.winAmount = winAmount;
            console.log('DEBUG: Setting game state win amount to:', winAmount);
            
            // Add the win amount to the user's balance
            updateJinuBalance(winAmount);
            
            // Show win message
            setGameResult({
              type: 'win',
              message: `Congratulations! You matched 3x ${winningValue} and won ${winAmount} JINU!`,
              amount: winAmount,
              matchedValue: winningValue
            });
            console.log('DEBUG: Set game result with amount:', winAmount, 'and matched value:', winningValue);
            
            toast({
              title: "🎉 WINNER! 🎉",
              description: (
                <Box textAlign="center">
                  <Text fontWeight="bold" fontSize="lg" mb={2}>
                    You matched 3x {winningValue}!
                  </Text>
                  <Text fontSize="2xl" color="yellow.300" fontWeight="bold">
                    +{winAmount} JINU
                  </Text>
                </Box>
              ),
              status: "success",
              duration: 6000,
              isClosable: true,
              position: "top",
              variant: "solid"
            });
          } else if (allRevealed) {
            // All cells revealed but no winning combination
            newState.status = 'lost';
            
            // Show lose message
            setGameResult({
              type: 'lose',
              message: "Better luck next time!"
            });
            
            toast({
              title: "No Win",
              description: "Better luck next time!",
              status: "info",
              duration: 3000,
              isClosable: true
            });
          }
        }
      }
      
      // Don't show duplicate toast messages - the one from checkForWinningCombination is enough
      setTimeout(() => {
        // Update the game state with win information
        setGameState(prevState => ({
          ...prevState,
          status: newState.status
        }));
        
        // Set game result for modal if needed
        if (winAmount > 0 && !gameResult) {
          console.log('DEBUG: Setting game result for modal, initial winAmount:', winAmount);
          
          // Get the matched value from the checkForWinningCombination function
          const matchedValue = checkForWinningCombination();
          console.log('DEBUG: Matched value from checkForWinningCombination:', matchedValue);
          
          // IMPORTANT: Use the actual matched value for the prize amount
          // This ensures the toast and modal show the correct prize
          const actualWinAmount = matchedValue ? getPrizeAmountFromMatchedValue(matchedValue) : winAmount;
          console.log('DEBUG: Actual win amount for modal:', actualWinAmount, 'from matched value:', matchedValue);
          
          setGameResult({
            type: 'win',
            message: `Congratulations! You matched 3x ${matchedValue || 'symbols'} and won ${actualWinAmount} JINU!`,
            amount: actualWinAmount,
            matchedValue: matchedValue
          });
          console.log('DEBUG: Set game result for modal with amount:', actualWinAmount);
          
          // Show win modal
          onOpen();
        }
      }, totalCells * 100 + 500); // Wait for all cells to be revealed plus a little extra time
    } else {
      // Not a winning card
      setTimeout(() => {
        setGameState(prevState => ({
          ...prevState,
          status: 'lost'
        }));
        
        toast({
          title: "All cells revealed",
          description: "Better luck next time!",
          status: "info",
          duration: 3000,
          isClosable: true
        });
      }, totalCells * 100 + 500);
    }
  };
  
  // Handle playing again
  const handlePlayAgain = () => {
    onClose();
    handleBuy();
  };
  
  // Handle gifting
  const handleGift = () => {
    // Implementation would depend on the gifting system
    // For now, we'll just show a toast
    toast({
      title: "Card purchased",
      description: "Click 'Reveal Winning Symbols' to start!",
      status: "success",
      duration: 3000,
      isClosable: true
    });
  };
  
  // Get bounty pool data based on card configuration
  const getBountyPoolData = () => {
    // If the card has a custom bounty pool configuration, use it
    if (cardConfig && cardConfig.bountyPool) {
      const bp = cardConfig.bountyPool;
      
      // Generate prize distribution from tiers
      const prizeDistribution = [];
      let totalWinners = 0;
      
      if (bp.tiers) {
        // Process each tier and prize
        bp.tiers.forEach(tier => {
          tier.prizes.forEach(prize => {
            totalWinners += prize.count;
            
            // Calculate odds (1 in X)
            const odds = Math.round(10000 / prize.count);
            
            // Add to prize distribution
            prizeDistribution.push({
              prize: `${prize.amount.toLocaleString()} JINU`,
              total: prize.count,
              claimed: Math.floor(prize.count * 0.3), // Simulate ~30% claimed
              remaining: Math.ceil(prize.count * 0.7), // Remaining
              odds: `1:${odds}`
            });
          });
        });
      }
      
      // Sort by prize amount (highest first)
      prizeDistribution.sort((a, b) => {
        const aAmount = parseInt(a.prize.replace(/[^0-9]/g, ''));
        const bAmount = parseInt(b.prize.replace(/[^0-9]/g, ''));
        return bAmount - aAmount;
      });
      
      // Generate prize summary
      const prizeSummary = {
        topPrizes: { total: 0, claimed: 0 },
        midPrizes: { total: 0, claimed: 0 },
        lowPrizes: { total: 0, claimed: 0 },
        specialRewards: { total: 0, claimed: 0 }
      };
      
      // Calculate summary numbers from tiers
      if (bp.tiers) {
        bp.tiers.forEach(tier => {
          let tierTotal = 0;
          let tierClaimed = 0;
          
          tier.prizes.forEach(prize => {
            tierTotal += prize.count;
            tierClaimed += Math.floor(prize.count * 0.3); // Simulate ~30% claimed
          });
          
          if (tier.name === 'Top') {
            prizeSummary.topPrizes.total = tierTotal;
            prizeSummary.topPrizes.claimed = tierClaimed;
          } else if (tier.name === 'Mid') {
            prizeSummary.midPrizes.total = tierTotal;
            prizeSummary.midPrizes.claimed = tierClaimed;
          } else if (tier.name === 'Low' || tier.name === 'Base') {
            prizeSummary.lowPrizes.total = tierTotal;
            prizeSummary.lowPrizes.claimed = tierClaimed;
          }
        });
      }
      
      // Add losing tickets to special rewards
      if (bp.losingTickets) {
        prizeSummary.specialRewards.total = bp.losingTickets.count;
        prizeSummary.specialRewards.claimed = Math.floor(bp.losingTickets.count * 0.2); // Simulate ~20% claimed
      }
      
      // Generate some recent claims
      const recentClaims = [
        { player: '0x7a9...3f4e', amount: 100000, timestamp: Date.now() - 1000 * 60 * 2, type: '100K' },
        { player: '0x3b2...9a7c', amount: 1000, timestamp: Date.now() - 1000 * 60 * 5, type: '1K' },
        { player: '0xf12...6e8d', amount: 500, timestamp: Date.now() - 1000 * 60 * 12, type: 'GIFT BOX' },
        { player: '0x8c4...2b1a', amount: 500, timestamp: Date.now() - 1000 * 60 * 18, type: '500' },
        { player: '0x5e7...9d3f', amount: 10000, timestamp: Date.now() - 1000 * 60 * 25, type: '10K' },
      ];
      
      // Calculate total cards and claimed cards
      const totalCards = totalWinners + (bp.losingTickets?.count || 0);
      const claimedCards = Math.floor(totalCards * 0.25); // Simulate ~25% claimed
      
      return {
        batchNumber: 1,
        cardsMinted: claimedCards,
        cardsRemaining: totalCards - claimedCards,
        totalCards: totalCards,
        totalPrizePool: bp.totalPrizePool,
        claimedPrizes: Math.floor(bp.totalPrizePool * 0.25), // Simulate ~25% claimed
        usdValue: Math.floor(bp.totalPrizePool * 0.015), // Simulate JINU to USD conversion
        winners: totalWinners,
        startDate: "2025-04-15",
        rtp: Math.round((bp.totalPrizePool / bp.totalSales) * 100),
        prizeDistribution,
        recentClaims,
        prizeSummary
      };
    }
    
    // Default bounty pool data if no configuration is available
    return {
      batchNumber: 1,
      cardsMinted: 12483,
      cardsRemaining: 37517,
      totalCards: 50000,
      totalPrizePool: 30000000,
      claimedPrizes: 7250000,
      usdValue: 450000,
      winners: 5844,
      startDate: "2025-04-15",
      rtp: 75,
      prizeDistribution: [
        { prize: '100K JINU', total: 5, claimed: 1, remaining: 4, odds: '1:10000' },
        { prize: '50K JINU', total: 10, claimed: 3, remaining: 7, odds: '1:5000' },
        { prize: '25K JINU', total: 20, claimed: 6, remaining: 14, odds: '1:2500' },
        { prize: '10K JINU', total: 100, claimed: 35, remaining: 65, odds: '1:500' },
        { prize: '7K JINU', total: 150, claimed: 52, remaining: 98, odds: '1:333' },
        { prize: '5K JINU', total: 200, claimed: 78, remaining: 122, odds: '1:250' },
        { prize: '3K JINU', total: 300, claimed: 112, remaining: 188, odds: '1:167' },
        { prize: '2K JINU', total: 500, claimed: 187, remaining: 313, odds: '1:100' },
        { prize: '1K JINU', total: 1000, claimed: 376, remaining: 624, odds: '1:50' },
        { prize: '500 JINU', total: 2000, claimed: 743, remaining: 1257, odds: '1:25' },
        { prize: '100 JINU', total: 12000, claimed: 4215, remaining: 7785, odds: '1:4.17' },
      ],
      recentClaims: [
        { player: '0x7a9...3f4e', amount: 100000, timestamp: Date.now() - 1000 * 60 * 2, type: '100K' },
        { player: '0x3b2...9a7c', amount: 1000, timestamp: Date.now() - 1000 * 60 * 5, type: '1K' },
        { player: '0xf12...6e8d', amount: 500, timestamp: Date.now() - 1000 * 60 * 12, type: 'GIFT BOX' },
        { player: '0x8c4...2b1a', amount: 500, timestamp: Date.now() - 1000 * 60 * 18, type: '500' },
        { player: '0x5e7...9d3f', amount: 10000, timestamp: Date.now() - 1000 * 60 * 25, type: '10K' },
      ],
      prizeSummary: {
        topPrizes: { total: 6, claimed: 1 },
        midPrizes: { total: 230, claimed: 80 },
        lowPrizes: { total: 16150, claimed: 5763 },
        specialRewards: { total: 32614, claimed: 6639 }
      }
    };
  };

  // Listen for clicks on the Scratch Card tab in the existing navigation
  useEffect(() => {
    // Function to handle clicks on the Scratch Card tab
    const handleScratchCardTabClick = (event) => {
      // Check if the clicked element is the Scratch Card tab
      if (event.target && event.target.textContent && 
          event.target.textContent.includes('Scratch Card') && 
          !event.target.closest('button[data-action="back-to-cards"]')) {
        
        // Prevent default navigation
        event.preventDefault();
        
        // Navigate back to all cards
        handleBackToAllCards();
      }
    };
    
    // Add event listener to the document
    document.addEventListener('click', handleScratchCardTabClick);
    
    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('click', handleScratchCardTabClick);
    };
  }, []);

  // Get the colors for the current card
  const cardColors = cardConfig ? getCardColors(cardConfig.id) : getCardColors('default');

  return (
    <Box
      width="100%"
      minHeight={inlineMode ? "auto" : "100vh"}
      bg={cardConfig?.id === 'cash_king' ? cardColors.primary : cardConfig?.id === 'jackpot_millions' ? cardColors.primary : "gray.900"}
      color={cardColors.text}
      p={4}
    >
      <Flex 
        direction={{ base: 'column', md: 'row' }}
        width="100%"
        gap={3}
      >
        {/* Game Area - Left side, bigger card */}
        <Box width={{ base: "100%", md: "auto" }}> {/* Fixed width card */}
          {cardId === 'cash_king' ? (
            // Use dedicated Cash King Game component for the green card
            <CashKingGame 
              isPreview={false}
              gameState={gameState}
              onBuy={handleBuy}
              onReveal={handleReveal}
            />
          ) : cardId === 'jackpot_millions' ? (
            // Use dedicated Jackpot Millions Game component for the purple card
            <JackpotMillionsGame 
              isPreview={false}
              gameState={gameState}
              onBuy={handleBuy}
              onReveal={handleReveal}
            />
          ) : cardConfig && (
            // Use standard ScratchCardDisplay for other cards
            <ScratchCardDisplay 
              cardType={cardId}
              isPreview={false}
              gameState={gameState}
              onBuy={handleBuy}
              onReveal={handleReveal}
            />
          )}
        </Box>
        
        {/* Game controls have been removed */}
      </Flex>
      
      {/* Bounty Pool - Full width, below card and controls */}
      <Box width="100%" mt={6}>
        {cardConfig && (
          <>
            {cardId === 'quick_cash' ? (
              <QuickCashBountyPool />
            ) : cardId === 'cash_king' ? (
              <CashKingBountyPool />
            ) : cardId === 'jackpot_millions' ? (
              <JackpotMillionsBountyPool />
            ) : (
              <BaseBountyPool 
                poolData={getBountyPoolData()}
                accentColor={(cardId === 'mega_millions' || cardId === 'quick_cash_premium') ? '#DC143C' : (cardConfig?.accentColor || "yellow.400")}
                bgColor={(cardId === 'mega_millions' || cardId === 'quick_cash_premium') ? 'linear-gradient(145deg, #660000, #8B0000)' : (cardConfig?.bgColor || "blue.900")}
              />
            )}
          </>
        )}
      </Box>
      
      {/* Win Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">🎉 Congratulations! 🎉</ModalHeader>
          <ModalBody>
            <VStack spacing={4}>
              {gameResult?.matchedValue && (
                <Text fontSize="lg" fontWeight="bold" textAlign="center" color="yellow.500">
                  You matched 3x {gameResult.matchedValue}!
                </Text>
              )}
              <Text fontSize="xl" fontWeight="bold" textAlign="center">
                You won {gameResult?.amount ? gameResult.amount.toLocaleString() : 0} JINU!
              </Text>
              <Text textAlign="center">
                Your winnings have been added to your balance.
              </Text>
              
              {/* Add the segment verifier if we have segment information */}
              {gameState && gameState.batchId && gameState.segmentId && (
                <Box mt={4} width="100%">
                  <Divider my={4} />
                  <SegmentVerifier 
                    batchId={gameState.batchId}
                    segmentId={gameState.segmentId}
                  />
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button colorScheme="green" onClick={handlePlayAgain}>
              Play Again
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ScratchCardGame;
